import {
  AfterContentInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";

import { uuid4 } from "@sentry/core";

import {
  ConfectTableBodyDirective,
  ConfectTableContentDirective,
  ConfectTableHeaderDirective,
  ConfectTableToolBarDirective,
} from "./confect-table.directive";

export type TableCallback = (hasMorePages: boolean) => void;

export interface TableHeader {
  key: string;
  name: string;
  size: number;
  locked?: boolean | null;
}

@Component({
  selector: "ngx-confect-table",
  templateUrl: "./confect-table.component.html",
  standalone: false,
})
export class ConfectTableComponent<T> implements OnDestroy, AfterContentInit {
  @Input() loading = false;
  scrollMax = 0;
  shouldLoadNextPage: boolean = true;

  _header: TableHeader[];
  lockedHeader: TableHeader[];
  lockedTableWidth: number;
  lockedTableRowHeight: number;
  _tableWidth;
  @Input() set tableWidth(to: number) {
    this._tableWidth = to;
    this.calcLockedTable();
  }
  @Input({ required: true }) set header(
    to:
      | { key?: string; name: string; size?: number; locked?: boolean }[]
      | string[],
  ) {
    if (typeof to[0] === "string") {
      this._header = to.map((name) => {
        return { name: name, key: name, size: 1 / to.length };
      });
      return;
    }

    let header = to as {
      key?: string;
      name: string;
      size?: number;
      locked?: boolean;
    }[];

    if (header[0].key == null) {
      header = to.map((col) => {
        col.key = col.name;
        return col;
      });
    }

    if (header[0].size == null) {
      header = to.map((col) => {
        return { name: col.name, key: col.key, size: 1 / to.length };
      });
    }

    this._header = header as TableHeader[];
    this.calcLockedTable();
  }
  _rows: Record<string, T>[];
  @Input({ required: true }) set rows(to: Record<string, T>[]) {
    this._rows = to;
    if (to != null) {
      this.shouldLoadNextPage = true;
    }
  }
  @Input() scroll: boolean = false;
  @Input() infiniteScroll: boolean = false;
  @Input() emptyState: string = "No items in this table";

  @Output() loadMore = new EventEmitter<TableCallback>();

  @ViewChild("table") table: ElementRef<any>;
  @ViewChild("tableBox") tableBox: ElementRef<any>;
  @ViewChild("tableFixedBox") tableFixedBox: ElementRef<any>;
  @ViewChild("tableRow") tableRow: ElementRef<any>;
  @ViewChild("lockedTableRow") lockedTableRow: ElementRef<any>;

  @ContentChild(ConfectTableBodyDirective, { read: TemplateRef })
  tableBody: TemplateRef<any>;
  @ContentChild(ConfectTableContentDirective, { read: TemplateRef })
  tableContent: TemplateRef<any>;
  @ContentChild(ConfectTableHeaderDirective, { read: TemplateRef })
  tableHeader: TemplateRef<any>;
  @ContentChild(ConfectTableToolBarDirective, { read: TemplateRef })
  tableToolBar: TemplateRef<any>;

  @HostListener("wheel", ["$event"])
  scrollEvent(event: Event) {
    if (!this.infiniteScroll) {
      return;
    }
    // if (this.scrollMax < this.table.nativeElement.scrollTopMax) {
    //   this.scrollMax = this.table.nativeElement.scrollTopMax;
    //   this.loading = false;
    // }

    const scrollTopMax =
      this.table.nativeElement.offsetHeight -
      this.tableBox.nativeElement.offsetHeight;
    const scrollTop = this.tableBox.nativeElement.scrollTop;

    const triggerLoad = (scrollTopMax - scrollTop) / scrollTopMax < 0.02;

    if (triggerLoad && !this.loading && this.shouldLoadNextPage) {
      this.loading = true;
      this.loadMore.emit(this.callback);
    }
  }

  calcLockedTable() {
    if (this._header == null || this._tableWidth == null) {
      return;
    }
    this.lockedHeader = (this._header as TableHeader[]).filter(
      (col) => col.locked,
    );
    this.lockedTableWidth =
      this.lockedHeader.reduce((acc, col) => acc + col.size, 0) *
      this._tableWidth;

    this._header = this._header.map((col) => {
      const colCopy = JSON.parse(JSON.stringify(col));
      if (colCopy.locked) {
        colCopy.key = uuid4();
        colCopy.name = "";
      }
      return colCopy;
    });
  }

  callback: TableCallback = (hasMorePages: boolean) => {
    this.shouldLoadNextPage = hasMorePages;
    this.loading = false;
  };

  ngAfterContentInit(): void {
    setTimeout(() => {
      this.lockedTableRowHeight = this.tableRow?.nativeElement.offsetHeight;
    });
  }

  ngOnDestroy(): void {
    if (this.table) {
      this.table.nativeElement.scrollTop = 0;
      this.table.nativeElement.scrollLet = 0;
    }

    if (this.tableBox) {
      this.tableBox.nativeElement.scrollTop = 0;
      this.tableBox.nativeElement.scrollLet = 0;
    }

    if (this.lockedTableRow) {
      this.lockedTableRow.nativeElement.scrollTop = 0;
      this.lockedTableRow.nativeElement.scrollLet = 0;
    }

    if (this.tableFixedBox) {
      this.tableFixedBox.nativeElement.scrollTop = 0;
      this.tableFixedBox.nativeElement.scrollLeft = 0;
    }
  }
  onScroll(event: WheelEvent) {
    if (event.deltaY !== 0) {
      this.tableBox.nativeElement.scrollTop += event.deltaY;
      this.lockedTableRow.nativeElement.scrollTop =
        this.tableBox.nativeElement.scrollTop;
    }
    if (event.deltaX !== 0) {
      this.tableFixedBox.nativeElement.scrollLeft += event.deltaX;
    }
  }
}
